/**
 * Deployment context, read from `GET /api/health`.
 *
 * The only consumer is the shell's stage marker, and that marker exists for a
 * specific failure: Vulcan publishes into a live engine, and the dev, test and
 * production apps look identical. Someone editing a flow in production while
 * believing they are in test is a production incident, so the stage is shown
 * whenever it is NOT production.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { apiFetch } from '../../../api/client.js'
import type { Health } from '../../../types/app.types.js'

const PRODUCTION_ENV = 'prod'

export const useAppStore = defineStore('app', () => {
  const health = ref<Health | null>(null)

  const stage = computed(() => health.value?.env ?? null)
  const showStage = computed(() => stage.value !== null && stage.value !== PRODUCTION_ENV)

  async function load(): Promise<void> {
    try {
      health.value = await apiFetch<Health>('/health')
    } catch {
      // A failed health probe is not worth a visible error: every page in the
      // app reports its own load failures, and a banner saying "health check
      // failed" on top of those adds noise, not information. The stage marker
      // simply stays hidden.
      health.value = null
    }
  }

  return { health, stage, showStage, load }
})
